/**
 * Project Parser Utility
 *
 * Parses project name strings into structured objects for better formatting.
 *
 * Pattern analysis from backend:
 * Full string: "TLB-0197-16 - AKER BP - IO REPORTS - REQUEST #16 - CODE MANAGEMENT"
 *
 * The project `name` field typically follows this structure:
 * - Client name (e.g., "AKER BP")
 * - Project category (e.g., "IO REPORTS")
 * - Request/task identifier (e.g., "REQUEST #16")
 * - Description (e.g., "CODE MANAGEMENT AND DEPLOY IMPROVEMENTS")
 *
 * Some projects may have additional parts like:
 * - Change number (e.g., "Change 2")
 * - Sub-description (e.g., "PBI Compliance and data visualisation")
 */

/**
 * Parsed project structure
 */
export interface ParsedProject {
  /** Original project code (e.g., "TLB-0197-16") */
  code: string;
  /** Full original name */
  fullName: string;
  /** Client/company name (e.g., "Aker BP") */
  client: string | null;
  /** Project category/type (e.g., "IO Reports") */
  category: string | null;
  /** Request identifier (e.g., "Request #16") */
  requestId: string | null;
  /** Main description/title */
  title: string;
  /** Change number if present (e.g., "Change 2") */
  changeNumber: string | null;
  /** Sub-description after change number */
  subDescription: string | null;
}

/**
 * Customer interface for matching (minimal, to avoid circular deps)
 */
interface CustomerLike {
  name: string;
}

/**
 * Find a matching customer name from the list (case-insensitive)
 * Returns the customer's proper name if found, null otherwise
 */
function findMatchingCustomer(
  candidateName: string,
  customers: CustomerLike[],
): string | null {
  const candidateUpper = candidateName.toUpperCase().trim();
  const match = customers.find(
    (c) => c.name.toUpperCase().trim() === candidateUpper,
  );
  return match ? match.name : null;
}

/**
 * Known project categories
 */
const KNOWN_CATEGORIES = [
  "IO REPORTS",
  "IO DASHBOARD",
  "DASHBOARD",
  "REPORTS",
  "ANALYSIS",
  "SUPPORT",
  "MAINTENANCE",
  "DEVELOPMENT",
  "CONSULTING",
];

/**
 * Title case converter - converts "HELLO WORLD" to "Hello World"
 */
export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => {
      // Keep certain words lowercase
      if (
        ["and", "or", "the", "a", "an", "of", "in", "on", "for"].includes(word)
      ) {
        return word;
      }
      // Keep acronyms uppercase if they're 2-4 chars
      if (
        word.length <= 4 &&
        word === word.toUpperCase() &&
        /^[A-Z]+$/.test(word.toUpperCase())
      ) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Parse a project name into structured parts
 *
 * @param code - Project code (e.g., "TLB-0197-16")
 * @param name - Project name (e.g., "AKER BP - IO REPORTS - REQUEST #16 - DESCRIPTION")
 * @param knownCustomers - Optional list of known customers to validate client extraction
 * @returns Parsed project object
 */
export function parseProjectName(
  code: string,
  name: string,
  knownCustomers?: CustomerLike[],
): ParsedProject {
  const result: ParsedProject = {
    code,
    fullName: name,
    client: null,
    category: null,
    requestId: null,
    title: name,
    changeNumber: null,
    subDescription: null,
  };

  if (!name) {
    return result;
  }

  // Split by " - " delimiter
  const parts = name.split(" - ").map((p) => p.trim());

  if (parts.length === 1) {
    // No delimiter, just use as title
    result.title = toTitleCase(parts[0]);
    return result;
  }

  let currentIndex = 0;

  // Treat the first part as the client when there are 2+ parts
  // If knownCustomers is provided, validate against the list for accurate extraction
  if (parts.length >= 2 && !parts[0].toLowerCase().startsWith("request")) {
    if (knownCustomers && knownCustomers.length > 0) {
      // Try to match against known customers for proper casing and validation
      const matchedCustomer = findMatchingCustomer(parts[0], knownCustomers);
      if (matchedCustomer) {
        // Use the customer's proper name from the database
        result.client = matchedCustomer;
        currentIndex = 1;
      }
      // If no match found with customers provided, don't extract as client
      // (the first part might not be a customer name)
    } else {
      // No customer list provided - use simple title case conversion
      result.client = toTitleCase(parts[0]);
      currentIndex = 1;
    }
  }

  // Try to identify category (second part)
  if (currentIndex < parts.length) {
    const partUpper = parts[currentIndex].toUpperCase();
    const matchedCategory = KNOWN_CATEGORIES.find(
      (cat) => partUpper === cat || partUpper.includes(cat),
    );

    if (matchedCategory || (parts.length >= 3 && currentIndex === 1)) {
      result.category = toTitleCase(parts[currentIndex]);
      currentIndex++;
    }
  }

  // Try to identify request ID (contains "REQUEST #" or "REQUEST")
  if (currentIndex < parts.length) {
    const requestMatch = parts[currentIndex].match(/^REQUEST\s*#?\s*(\d+)/i);
    if (requestMatch) {
      result.requestId = `Request #${requestMatch[1]}`;
      currentIndex++;
    }
  }

  // Check for change number in remaining parts
  const remainingParts = parts.slice(currentIndex);
  const changeIndex = remainingParts.findIndex((p) => /^CHANGE\s+\d+/i.test(p));

  if (changeIndex !== -1) {
    // Parts before change number are the title
    const titleParts = remainingParts.slice(0, changeIndex);
    result.title = titleParts.map((p) => toTitleCase(p)).join(" - ");

    // Change number
    const changeMatch = remainingParts[changeIndex].match(/^(CHANGE\s+\d+)/i);
    if (changeMatch) {
      result.changeNumber = toTitleCase(changeMatch[1]);
    }

    // Parts after change number are sub-description
    const afterChange = remainingParts[changeIndex].replace(
      /^CHANGE\s+\d+\s*[-–]\s*/i,
      "",
    );
    const subParts = [
      ...(afterChange ? [afterChange] : []),
      ...remainingParts.slice(changeIndex + 1),
    ];

    if (subParts.length > 0) {
      result.subDescription = subParts.map((p) => toTitleCase(p)).join(" - ");
    }
  } else {
    // No change number, rest is the title
    result.title = remainingParts.map((p) => toTitleCase(p)).join(" - ");
  }

  // If title is empty, use full name
  if (!result.title) {
    result.title = toTitleCase(name);
  }

  return result;
}

/**
 * Format a parsed project for display (short version)
 * Shows: code + title
 */
export function formatProjectShort(parsed: ParsedProject): string {
  return `${parsed.code} - ${parsed.title}`;
}

/**
 * Format a parsed project for display (medium version)
 * Shows: code + client + title
 */
export function formatProjectMedium(parsed: ParsedProject): string {
  const parts = [parsed.code];
  if (parsed.client) parts.push(parsed.client);
  parts.push(parsed.title);
  return parts.join(" - ");
}

/**
 * Format a parsed project for display (full version)
 * Shows all parts
 */
export function formatProjectFull(parsed: ParsedProject): string {
  return `${parsed.code} - ${parsed.fullName}`;
}

/**
 * Get a display-friendly project label for dropdowns
 */
export function getProjectDisplayLabel(code: string, name: string): string {
  const parsed = parseProjectName(code, name);
  // For dropdown, show abbreviated version
  if (parsed.client && parsed.title) {
    return `${parsed.code} - ${parsed.client} - ${parsed.title}`;
  }
  return `${parsed.code} - ${parsed.title}`;
}
